import Image from "next/image"
import QRCode from "qrcode"
import { UserPlus } from "lucide-react"
import { Section } from "@/components/layout/Section"
import { Container } from "@/components/layout/Container"
import type { Locale } from "@/lib/i18n"

// Seeded PRNG so server and client render the exact same binary pattern (no hydration mismatch)
function binaryRows(count: number, length: number, seed: number) {
  let s = seed
  const rand = () => ((s = (s * 1103515245 + 12345) % 2147483648) / 2147483648)
  return Array.from({ length: count }, () =>
    Array.from({ length }, () => {
      const r = rand()
      return r < 0.08 ? " " : r < 0.54 ? "0" : "1"
    }).join("")
  )
}

const FRONT_ROWS = binaryRows(16, 64, 7)
const BACK_ROWS = binaryRows(3, 64, 42)

const card = {
  firstName: "Kubilay",
  lastName: "Akkız",
  org: "LAKVERSE",
  title: { tr: "Yazılım Geliştirici", en: "Software Developer" },
  phones: [
    { display: "0531 722 14 89", tel: "+905317221489" },
  ],
  email: "kubilay@lakverse.com",
  website: "lakverse.com",
}

// vCard 3.0: phones' camera apps offer "add to contacts" when this is scanned
function buildVCard(locale: Locale) {
  return [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `N:${card.lastName};${card.firstName};;;`,
    `FN:${card.firstName} ${card.lastName}`,
    `ORG:${card.org}`,
    `TITLE:${card.title[locale] ?? card.title.tr}`,
    ...card.phones.map((p, i) => `TEL;TYPE=${i === 0 ? "CELL" : "WORK"},VOICE:${p.tel}`),
    `EMAIL;TYPE=INTERNET:${card.email}`,
    `URL:https://${card.website}`,
    "END:VCARD",
  ].join("\r\n")
}

// QR drawn as a single SVG path from the module matrix (sync, identical on server and client)
function qrPath(text: string) {
  const { modules } = QRCode.create(text, { errorCorrectionLevel: "M" })
  const n = modules.size
  let d = ""
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      if (modules.get(y, x)) d += `M${x} ${y}h1v1h-1z`
    }
  }
  return { d, size: n }
}

const copy = {
  tr: {
    heading: "Kartvizit",
    description: "Kurumsal kimliğimizi taşıyan kartvizit tasarımımız. Ana renk, vurgu rengi ve JetBrains Mono ile hazırlanmıştır.",
    front: "Ön Yüz",
    back: "Arka Yüz",
    size: "Ölçü: 85 × 55 mm",
    scan: "Rehbere ekle",
    addContact: "Rehbere Ekle",
  },
  en: {
    heading: "Business Card",
    description: "Our business card carries our corporate identity, built with our primary color, accent color and JetBrains Mono.",
    front: "Front",
    back: "Back",
    size: "Size: 85 × 55 mm",
    scan: "Save contact",
    addContact: "Save Contact",
  },
}

// Font sizes use container query units so the card scales as one piece at any width
const cq = (n: number) => ({ fontSize: `${n}cqw` })

function BinaryPattern({ rows, className = "" }: { rows: string[]; className?: string }) {
  return (
    <div aria-hidden className={`font-mono select-none whitespace-pre leading-[1.3] ${className}`} style={cq(3.1)}>
      {rows.map((row, i) => (
        <div key={i} className={i % 3 === 1 ? "text-sky-300/55" : "text-sky-400/35"}>
          {row}
        </div>
      ))}
    </div>
  )
}

function CardFace({ children, label }: { children: React.ReactNode; label: string }) {
  return (
    <figure className="space-y-3">
      <div
        className="@container relative w-full overflow-hidden rounded-xl bg-[#034AA6] shadow-2xl shadow-[#022859]/30 ring-1 ring-black/5"
        style={{ aspectRatio: "85 / 55" }}
      >
        {children}
      </div>
      <figcaption className="text-center text-sm font-mono text-slate-500">{label}</figcaption>
    </figure>
  )
}

export function BusinessCard({ locale = "tr" }: { locale?: Locale }) {
  const t = copy[locale] ?? copy.tr
  const vcard = buildVCard(locale)
  const qr = qrPath(vcard)

  return (
    <Section className="py-16 lg:py-24 bg-white">
      <Container>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 font-mono">{t.heading}</h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">{t.description}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-14">
            {/* Front */}
            <CardFace label={t.front}>
              <BinaryPattern rows={FRONT_ROWS} className="absolute -left-[2%] -top-[1%]" />
              <div className="absolute inset-0 flex items-center pl-[16%]">
                <span
                  className="font-mono font-bold lowercase tracking-tight text-white [text-shadow:0_0_0.6em_#034AA6,0_0_0.25em_#034AA6]"
                  style={cq(9)}
                >
                  full time geek
                </span>
              </div>
              <Image
                src="/LAKVERSE-white.png"
                alt="LAKVERSE"
                width={240}
                height={80}
                className="absolute bottom-[7%] right-[6%] h-auto w-[22%] opacity-90"
              />
            </CardFace>

            {/* Back */}
            <CardFace label={t.back}>
              <div className="absolute inset-x-0 top-0 [mask-image:linear-gradient(to_bottom,black_40%,transparent)]">
                <BinaryPattern rows={BACK_ROWS} className="-ml-[2%]" />
              </div>
              <div className="absolute inset-x-[7%] bottom-[9%] top-[27%] flex items-center justify-between gap-[5%] font-mono text-white">
                <div className="min-w-0">
                  <div className="font-bold tracking-tight leading-tight" style={cq(6.4)}>
                    {card.firstName} {card.lastName}
                  </div>
                  <div className="mt-[2%] tracking-[0.1em] text-white/85" style={cq(3.4)}>
                    {card.title[locale] ?? card.title.tr}
                  </div>
                  <div className="mt-[9%] space-y-[1.5%] leading-snug" style={cq(3.1)}>
                    {card.phones.map((p) => (
                      <a key={p.tel} href={`tel:${p.tel}`} className="block hover:text-sky-200">
                        {p.display}
                      </a>
                    ))}
                    <a href={`mailto:${card.email}`} className="block hover:text-sky-200">
                      {card.email}
                    </a>
                    <a href={`https://${card.website}`} className="block text-white/80 hover:text-sky-200">
                      {card.website}
                    </a>
                  </div>
                </div>
                <div className="flex w-[30%] shrink-0 flex-col items-center gap-[4%]">
                  <svg
                    viewBox={`-2 -2 ${qr.size + 4} ${qr.size + 4}`}
                    className="w-full rounded-[6%] bg-white"
                    shapeRendering="crispEdges"
                    role="img"
                    aria-label={t.scan}
                  >
                    <path d={qr.d} fill="#022859" />
                  </svg>
                  <span className="text-white/80" style={cq(2.4)}>
                    {t.scan}
                  </span>
                </div>
              </div>
            </CardFace>
          </div>

          <div className="mt-8 flex flex-col items-center gap-4">
            <a
              href={`data:text/vcard;charset=utf-8,${encodeURIComponent(vcard)}`}
              download={`${card.firstName}-${card.lastName}.vcf`}
              className="inline-flex items-center gap-2 rounded-lg bg-[#034AA6] px-5 py-2.5 text-sm font-mono font-semibold text-white hover:bg-[#022859] transition-colors"
            >
              <UserPlus className="h-4 w-4" />
              {t.addContact}
            </a>
            <p className="text-sm font-mono text-slate-500">{t.size}</p>
          </div>
        </div>
      </Container>
    </Section>
  )
}
